import { create } from 'zustand';
import { fetchCurrentLicense } from '../api/currentLicenseClient';

export interface ExportQuotaState {
  /** True once any server response reported the entitlement. While false
   *  the UI must not gate exports — the server stays authoritative. */
  known: boolean;
  /** Demo classification of the resolved runtime license. */
  isDemo: boolean;
  /** Expiration from the same current-license response as the quota. */
  dueTime: string | null;
  /** Remaining exportable Revit elements; `null` = unlimited (when known). */
  remainingExportElements: number | null;
  /** Monotonic update counter, bumped by activation resets and by every
   *  run-response update. An in-flight current-license fetch compares it
   *  to decide whether its completion is still current — a slow GET must
   *  never overwrite newer, more authoritative quota state. */
  revision: number;
}

interface ExportQuotaActions {
  /** Starts a fresh auth/workspace activation: the display returns to
   *  unknown/available and any still-pending current-license fetch from
   *  the previous activation is invalidated. A stale entitlement from an
   *  earlier session must not gate a newly activated user, and a failed
   *  fetch after this leaves the server as the only enforcement point. */
  activate: () => void;
  /** Refreshes the entitlement from GET /api/licenses/current. A failed
   *  or invalid fetch keeps the current state; a completion that lost the
   *  race against a run-response update or a newer activation is
   *  discarded. */
  loadCurrentLicense: () => Promise<boolean>;
  /** Applies the quota a PCF-run response reported. `undefined` (no usable
   *  quota in the response) leaves the display untouched. */
  applyRunQuota: (
    remainingExportElements: number | null | undefined,
    isDemo?: boolean,
  ) => void;
  /** Applies the server balance carried by a quota rejection. The same
   *  machine-readable code is used for a zero balance and for a request that
   *  exceeds a positive balance, so only an explicit zero may gate exports. */
  applyExhausted: (isDemo?: boolean, remainingExportElements?: number | null) => void;
}

const initialState = {
  known: false,
  isDemo: false,
  dueTime: null as string | null,
  remainingExportElements: null as number | null,
};

export const useExportQuotaStore = create<ExportQuotaState & ExportQuotaActions>((set, get) => ({
  ...initialState,
  revision: 0,

  activate: () =>
    set((state) => ({ ...initialState, revision: state.revision + 1 })),

  loadCurrentLicense: async () => {
    const startRevision = get().revision;
    try {
      const license = await fetchCurrentLicense();
      if (get().revision !== startRevision) {
        // A run response or a newer activation advanced the store while
        // this fetch was in flight — its result is stale, drop it.
        return get().known;
      }
      set({
        known: true,
        isDemo: license.isDemo,
        dueTime: license.dueTime,
        remainingExportElements: license.remainingExportElements,
      });
      return true;
    } catch {
      // Keep the current state; the server enforces the quota.
      return false;
    }
  },

  applyRunQuota: (remainingExportElements, isDemo) => {
    if (remainingExportElements === undefined) {
      return;
    }
    set((state) => ({
      known: true,
      remainingExportElements,
      isDemo: isDemo ?? state.isDemo,
      revision: state.revision + 1,
    }));
  },

  applyExhausted: (isDemo, remainingExportElements) =>
    set((state) => ({
      known: true,
      // Older callers did not receive the response balance. Preserve their
      // safe zero fallback, but never erase an explicit positive balance.
      remainingExportElements: remainingExportElements === undefined
        ? 0
        : remainingExportElements,
      isDemo: isDemo ?? state.isDemo,
      revision: state.revision + 1,
    })),
}));

/** The license is quota-limited and used up — export actions must be
 *  disabled and the exhaustion guidance shown. */
export const selectQuotaExhausted = (state: ExportQuotaState): boolean =>
  state.known && state.remainingExportElements === 0;

/** The license carries a finite allowance — the remaining counter should
 *  be visible. Unlimited or unknown entitlements render no number. */
export const selectQuotaLimited = (state: ExportQuotaState): boolean =>
  state.known && state.remainingExportElements !== null;
